import { useTranslation } from 'react-i18next';
import { useCompanyTimeZone, useFormatDateTime } from '../../hooks/useFormatDate.js';

/**
 * Build/version stamp shown at the bottom of the sidebar. Answers "am I on the
 * latest deploy?" at a glance — it prints the commit short-SHA, the commit
 * title, and the build date baked in at build time (vite.config.ts). Because
 * the web app is a PWA that can serve a cached build, this is the reliable way
 * to confirm what's actually running versus what was last pushed.
 *
 * It is developer metadata, so 12px (`text-xs`) is the right size for it — but
 * it is not exempt from the floor either. It shipped at 11px.
 *
 * It sits at the foot of the nav rail, so its ink comes from
 * `--nav-on-surface` — the token paired with the rail's own `--nav-surface` —
 * not from `--muted-foreground`/`--foreground`, which are inks for a neutral
 * surface and measure 1.85:1 and 1.00:1 against the rail in light theme (the
 * rail IS `--foreground`'s colour there).
 *
 * It used to draw from `--primary-foreground`, back when the rail was a slab of
 * `--primary`. The nav split off its own tokens and this was left behind, which
 * nothing caught because in light `--primary-foreground` is white and so is
 * `--nav-on-surface` — identical output, passing by coincidence. In dark
 * `--primary-foreground` flips to #2f009b while the rail stays ink by design,
 * and the stamp went to 1.46:1 / 1.27:1. Measured over the rail:
 *
 *                          light            dark
 *   commit SHA (full)      17.16:1        19.29:1   (was 17.16 / 1.46)
 *   date + message `/75`   10.05:1        10.91:1   (was 10.05 / 1.27)
 *
 * The `/15` top border is a decorative hairline, like `--border` itself, and is
 * not held to a contrast floor.
 */
/**
 * The build stamp in the tenant's own date + clock, like every other stamp in
 * the product. It was `toLocaleString(undefined, …)` — the DEVICE's date order
 * and hour cycle — so the rail's footer printed a 12-hour time under a 24-hour
 * screen. The raw ISO survives an unparseable value: a build stamp we cannot
 * read is worth showing verbatim to whoever is debugging it.
 */
function useBuildDate(): (iso: string) => string {
  const formatDateTime = useFormatDateTime();
  return (iso) => formatDateTime(iso) || iso;
}

/** `rail` sits on the ink navigation column; `page` on the phone's profile
 *  page, where the rail is not drawn and the stamp moved with its rows. */
export function AppVersion({ tone = 'rail' }: { tone?: 'rail' | 'page' }): JSX.Element {
  const { t } = useTranslation();
  const formatBuildDate = useBuildDate();
  const timeZone = useCompanyTimeZone();
  const commit = __BUILD_COMMIT__;
  const message = __BUILD_MESSAGE__;
  const date = formatBuildDate(__BUILD_DATE__);

  return (
    <div
      className={
        tone === 'rail'
          ? 'shrink-0 border-t border-nav-border px-4 py-2 text-dense leading-tight text-nav-on-surface-muted'
          : 'text-label leading-tight text-foreground-tertiary'
      }
      /* The zone belongs to the timestamp and there is no room for it on a
         252px rail beside a SHA, so it is spelled out here — in full, as the
         IANA name the tenant configured, not an abbreviation this app would
         have to invent. docs/time-and-dates.md: the moment is the record, the
         zone is how it is read, and the company's zone is the one the rest of
         the product reads every other stamp in. */
      title={t('appVersion.tooltip', 'Commit {{commit}}: {{message}}\nBuilt {{when}} ({{zone}})', {
        commit,
        message,
        when: date,
        zone: timeZone,
      })}
    >
      {/* THE SHA AND THE DATE ARE SHOWN; THE COMMIT SUBJECT IS NOT.
          This used to render the message as a second line, so every site admin
          and HR user had "fix(employees): prose out of the definition grid" in
          their navigation — a sentence written for this repository, addressed
          to nobody using the product.
          The stamp itself stays, because the need behind it is real and
          operational: the web app is a PWA that can serve a cached build, and
          asking someone to read four characters back is how support tells "you
          are on the old bundle" from "the fix did not work". That need is met
          by the SHA and the date. The message is kept in the `title` above, so
          it is one hover away for whoever actually wants it. */}
      {/* "b3dfa15 · 06-09-2026 06:19" SAID WHAT, EXACTLY? The date was bare, so
          the one line in the product that answers "am I on the latest deploy?"
          could equally have been read as a last-refreshed or a last-sync time —
          and the owner read it as one. It is the BUILD's timestamp, baked in by
          vite.config.ts at the moment the bundle was produced, and it now says
          so. The word is the smallest thing that makes the number mean
          something; the zone it is rendered in follows in the tooltip above. */}
      <p className="flex items-center gap-control">
        <span className={tone === 'rail' ? 'font-mono font-medium text-nav-on-surface' : 'font-mono font-medium text-foreground'}>{commit}</span>
        <span aria-hidden>·</span>
        <span>{t('appVersion.built', 'Built {{when}}', { when: date })}</span>
      </p>
    </div>
  );
}
